import React, { useState, useRef } from 'react';
import { generateArtwork } from '../lib/gemini';
import { Image as ImageIcon, Loader2, Wand2, ArrowRight, XCircle } from 'lucide-react';
import { BrandProfile } from '../types';

export const CarouselTester = ({ brands }: { brands: BrandProfile[] }) => {
  const [activeBrandId, setActiveBrandId] = useState<string>(brands[0]?.id || '');
  const [baseConcept, setBaseConcept] = useState('Abstract flowing gradients with deep galaxy purple and gold accents');
  const [slidesCount, setSlidesCount] = useState(3);
  const [slideConcepts, setSlideConcepts] = useState([
    'Text overlay: "The Origin of Stars" centered bold typography.',
    'A glowing nebula element on the right, Text: "Nebula genesis".',
    'Text: "Endless expanding universe" with small stars forming a constellation'
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [baseImage, setBaseImage] = useState<string | null>(null);
  const [finalSlides, setFinalSlides] = useState<string[]>([]);
  const [status, setStatus] = useState<string>('');

  const cancelRef = useRef(false);

  const brand = brands.find(b => b.id === activeBrandId);

  const handleSlideConceptChange = (index: number, val: string) => {
    const newConcepts = [...slideConcepts];
    newConcepts[index] = val;
    setSlideConcepts(newConcepts);
  };

  const cropImageToSlices = async (imageUrl: string, numSlices: number): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const sliceWidth = img.width / numSlices;
        const sliceHeight = sliceWidth; // Force square slices
        const startY = (img.height - sliceHeight) / 2; // Center vertically
        
        const slices: string[] = [];

        for (let i = 0; i < numSlices; i++) {
          const canvas = document.createElement('canvas');
          canvas.width = sliceWidth;
          canvas.height = sliceHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, i * sliceWidth, startY, sliceWidth, sliceHeight, 0, 0, sliceWidth, sliceHeight);
            slices.push(canvas.toDataURL('image/jpeg', 0.8));
          }
        }
        resolve(slices);
      };
      img.onerror = () => reject(new Error('Failed to load image for cropping'));
      img.src = imageUrl;
    });
  };

  const handleGenerateBase = async () => {
    if (!brand) return;
    setIsGenerating(true);
    cancelRef.current = false;
    setFinalSlides([]);
    setBaseImage(null);
    setStatus('Generating wide base panorama...');

    try {
      const baseArtworkUrl = await generateArtwork(
        `STRICT RULE: Generate a single, unbroken landscape wallpaper. DO NOT draw panels, borders, vertical lines, frames, or layouts. DO NOT mockup a carousel. NO TEXT. NO TYPOGRAPHY. NO LETTERS. It must be a clean, seamless, continuous texture or scene from the far left to the far right. CONCEPT: ${baseConcept}`,
        undefined, // NO LOGO
        "16:9",
        [],
        [],
        undefined,
        undefined,
        undefined,
        brand.id
      );

      if (cancelRef.current) {
        setStatus('Generation cancelled.');
        setIsGenerating(false);
        return;
      }

      setBaseImage(baseArtworkUrl);
      setStatus('Base panorama ready. Review and proceed, or regenerate.');
    } catch (err) {
      console.error(err);
      setStatus('Error generating base image. Try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateSlides = async () => {
    if (!brand || !baseImage) return;
    setIsGenerating(true);
    cancelRef.current = false;
    setFinalSlides([]);
    setStatus('Slicing base image...');

    try {
      const slicesBase64 = await cropImageToSlices(baseImage, slidesCount);

      setStatus('Overlaying content onto individual slides...');
      const completedSlides: string[] = [];
      
      for (let i = 0; i < slidesCount; i++) {
        if (cancelRef.current) {
          setStatus('Generation stopped by user.');
          break;
        }

        setStatus(`Overlaying content onto slide ${i + 1} of ${slidesCount}...`);
        const sliceData = slicesBase64[i];
        const refinementPrompt = `TASK: Add typography and specific elements to this background panel. STRICT RULE: Keep the exact same background colors, patterns, and edges so it connects perfectly to adjacent panels. DO NOT alter or shift the background. Blend the following seamlessly into the design: ${slideConcepts[i]}`;
        
        const refinedSlideUrl = await generateArtwork(
          slideConcepts[i],
          i === slidesCount - 1 ? brand.logoUrl : undefined,
          "1:1",
          [],
          [],
          undefined,
          sliceData,
          refinementPrompt,
          brand.id
        );

        if (cancelRef.current) {
          setStatus('Generation stopped by user.');
          break;
        }

        completedSlides.push(refinedSlideUrl);
        setFinalSlides([...completedSlides]);
      }

      if (!cancelRef.current) {
        setStatus('Complete!');
      }
    } catch (err) {
      console.error(err);
      setStatus('Error generating slides.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStop = () => {
    cancelRef.current = true;
    setIsGenerating(false);
    setStatus('Stopping generation...');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black tracking-tight text-gray-900 border-b pb-4 mb-6">Seamless Carousel Generator (Alpha)</h1>
        <p className="text-sm text-gray-500">Test the background-slice-refine method for seamless carousels before integrating it into the core generation queue.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6">
        <div>
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">Select Brand Context</label>
          <select 
            value={activeBrandId}
            onChange={(e) => setActiveBrandId(e.target.value)}
            className="w-full p-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
          >
            {brands.map(b => (
              <option key={b.id} value={b.id}>{b.companyName}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">Base Panoramic Vibe (The underlying seamless background)</label>
          <textarea 
            className="w-full p-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm h-24"
            value={baseConcept}
            onChange={(e) => setBaseConcept(e.target.value)}
            placeholder="E.g. A continuous abstract gradient flowing from warm orange to deep indigo..."
          />
        </div>

        <div className="space-y-4">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">Slides ({slidesCount})</label>
          {slideConcepts.map((concept, idx) => (
            <div key={idx} className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-500 shrink-0 mt-1">
                {idx + 1}
              </div>
              <textarea 
                className="w-full p-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm h-20"
                value={concept}
                onChange={(e) => handleSlideConceptChange(idx, e.target.value)}
                placeholder={`Specific elements or typography for slide ${idx + 1}...`}
              />
            </div>
          ))}
          {slidesCount < 6 && (
            <button 
              onClick={() => { setSlidesCount(prev => prev + 1); setSlideConcepts([...slideConcepts, '']); }}
              className="text-sm font-bold text-indigo-600 hover:text-indigo-700"
            >
              + Add Slide
            </button>
          )}
        </div>

        <div className="flex gap-4">
          {isGenerating ? (
            <button 
              onClick={handleStop}
              className="w-full bg-red-600 text-white font-bold py-4 rounded-xl hover:bg-red-700 flex items-center justify-center gap-2 transition-all active:scale-98"
            >
              <XCircle className="w-5 h-5 animate-pulse" />
              Stop Generation Task
            </button>
          ) : (
            <>
              <button 
                onClick={handleGenerateBase}
                className="flex-1 bg-indigo-600 text-white font-bold py-4 rounded-xl hover:bg-indigo-700 flex items-center justify-center gap-2 transition-all active:scale-98"
              >
                <Wand2 className="w-5 h-5" />
                1. Generate Base Panorama
              </button>

              <button 
                onClick={handleGenerateSlides}
                disabled={!baseImage}
                className="flex-1 bg-fuchsia-600 text-white font-bold py-4 rounded-xl hover:bg-fuchsia-700 flex items-center justify-center gap-2 disabled:bg-fuchsia-400 disabled:opacity-50 transition-all active:scale-98"
              >
                <ArrowRight className="w-5 h-5" />
                2. Overlay Slides
              </button>
            </>
          )}
        </div>

        {status && (
          <div className="text-center text-sm font-medium text-gray-500 animate-pulse">
            {status}
          </div>
        )}
      </div>

      {(baseImage || finalSlides.length > 0) && (
        <div className="space-y-6 pt-6 border-t border-gray-200">
          <h2 className="text-xl font-bold">Results</h2>
          
          {baseImage && (
            <div className="space-y-2">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">1. Base 16:9 Generated Image</span>
              <img src={baseImage} className="w-full rounded-xl border border-gray-200 shadow-sm" alt="Base panorama" />
            </div>
          )}

          {finalSlides.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">2. Overlaid / Sliced Final Slides</span>
              <div className="flex overflow-x-auto gap-0 bg-gray-50 p-8 rounded-2xl border border-gray-100 shadow-inner">
                {finalSlides.map((slide, i) => (
                  <div key={i} className="flex-shrink-0 w-64 h-64 relative group">
                    <img src={slide} className="w-full h-full object-cover shadow-2xl transition-transform hover:scale-105 hover:z-10 relative cursor-pointer" alt={`Slide ${i + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
